import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import {
  FileText, Download, Calendar, IndianRupee, TrendingUp, Award,
  ArrowLeft, ChevronRight, Printer, CheckCircle, Clock, Building,
  User, Mail, CreditCard, Minus, Plus
} from 'lucide-react';
import ObserverNav from '../components/ObserverNav';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const ObserverPayslips = () => {
  const navigate = useNavigate();
  const { year, month } = useParams();
  
  const [payslips, setPayslips] = useState([]);
  const [selectedPayslip, setSelectedPayslip] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedYear, setSelectedYear] = useState(year ? parseInt(year) : new Date().getFullYear());
  const [selectedMonth, setSelectedMonth] = useState(month ? parseInt(month) : new Date().getMonth() + 1);

  const getToken = () => localStorage.getItem('observer_token');

  useEffect(() => {
    const token = getToken();
    if (!token) {
      navigate('/observer/login');
      return;
    }
    loadPayslips();
  }, [navigate]);

  useEffect(() => {
    if (year && month) {
      loadSpecificPayslip(parseInt(year), parseInt(month));
    }
  }, [year, month]);

  const loadPayslips = async () => {
    try {
      const token = getToken();
      const response = await axios.get(`${BACKEND_URL}/api/observer/payslips?token=${token}`);
      setPayslips(response.data.payslips || []);
    } catch (error) {
      console.error('Error loading payslips:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadSpecificPayslip = async (yr, mo) => {
    try {
      const token = getToken();
      const response = await axios.get(`${BACKEND_URL}/api/observer/payslip/${yr}/${mo}?token=${token}`);
      setSelectedPayslip(response.data);
      setSelectedYear(yr);
      setSelectedMonth(mo);
    } catch (error) {
      console.error('Error loading payslip:', error);
    }
  };

  const generatePayslip = async () => {
    try {
      setLoading(true);
      await loadSpecificPayslip(selectedYear, selectedMonth);
      loadPayslips();
    } catch (error) {
      console.error('Error generating payslip:', error);
      alert('Failed to generate payslip');
    } finally {
      setLoading(false);
    }
  };

  const printPayslip = () => {
    window.print();
  };

  const months = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
  ];

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <ObserverNav />
        <div className="pt-20 flex items-center justify-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-violet-600"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-violet-50 to-purple-100" data-testid="observer-payslips-page">
      <ObserverNav />
      
      <div className="pt-4 pb-8 px-4">
        <div className="max-w-5xl mx-auto">
          {/* Header */}
          <div className="flex items-center justify-between mb-6">
            <div>
              <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
                <FileText className="w-7 h-7 text-violet-600" />
                Pay Slips
              </h1>
              <p className="text-gray-600">View and download your monthly pay slips</p>
            </div>
            <Button
              variant="outline"
              onClick={() => navigate('/observer/earnings')}
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Earnings
            </Button>
          </div>

          {/* Month Selector */}
          <Card className="mb-6">
            <CardContent className="p-4">
              <div className="flex flex-wrap items-center gap-4">
                <div>
                  <label className="text-sm text-gray-600 block mb-1">Year</label>
                  <select
                    value={selectedYear}
                    onChange={(e) => setSelectedYear(parseInt(e.target.value))}
                    className="px-3 py-2 border rounded-lg"
                  >
                    {[2024, 2025, 2026].map(y => (
                      <option key={y} value={y}>{y}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="text-sm text-gray-600 block mb-1">Month</label>
                  <select
                    value={selectedMonth}
                    onChange={(e) => setSelectedMonth(parseInt(e.target.value))}
                    className="px-3 py-2 border rounded-lg"
                  >
                    {months.map((m, idx) => (
                      <option key={idx} value={idx + 1}>{m}</option>
                    ))}
                  </select>
                </div>
                <div className="flex-1" />
                <Button onClick={generatePayslip} className="bg-violet-600 hover:bg-violet-700">
                  <FileText className="w-4 h-4 mr-2" />
                  Generate Pay Slip
                </Button>
              </div>
            </CardContent>
          </Card>

          <div className="grid lg:grid-cols-3 gap-6">
            {/* Pay Slip History */}
            <div className="lg:col-span-1">
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">Pay Slip History</CardTitle>
                </CardHeader>
                <CardContent className="p-0">
                  {payslips.length === 0 ? (
                    <div className="p-6 text-center text-gray-500">
                      <FileText className="w-12 h-12 mx-auto mb-2 text-gray-300" />
                      <p>No pay slips yet</p>
                    </div>
                  ) : (
                    <div className="divide-y">
                      {payslips.map((ps, idx) => (
                        <div
                          key={ps.id || idx}
                          className={`p-4 cursor-pointer hover:bg-gray-50 transition-colors ${
                            selectedPayslip?.id === ps.id ? 'bg-violet-50 border-l-4 border-violet-600' : ''
                          }`}
                          onClick={() => setSelectedPayslip(ps)}
                        >
                          <div className="flex items-center justify-between">
                            <div>
                              <p className="font-medium text-gray-900">{ps.month_name}</p>
                              <p className="text-sm text-gray-500">{ps.payslip_number}</p>
                            </div>
                            <div className="text-right">
                              <p className="font-bold text-green-600">₹{ps.net_earnings?.toLocaleString()}</p>
                              <span className={`text-xs px-2 py-0.5 rounded-full ${
                                ps.status === 'paid' ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'
                              }`}>
                                {ps.status === 'paid' ? 'Paid' : 'Pending'}
                              </span>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>

            {/* Pay Slip Detail */}
            <div className="lg:col-span-2">
              {selectedPayslip ? (
                <Card className="print:shadow-none" id="payslip-content">
                  {/* Company Header */}
                  <div className="bg-gradient-to-r from-violet-600 to-purple-600 text-white p-6 print:bg-violet-600">
                    <div className="flex items-center justify-between">
                      <div>
                        <h2 className="text-xl font-bold">{selectedPayslip.company?.name}</h2>
                        <p className="text-violet-200 text-sm">{selectedPayslip.company?.address}</p>
                      </div>
                      <div className="text-right">
                        <p className="text-2xl font-bold">PAY SLIP</p>
                        <p className="text-violet-200">{selectedPayslip.month_name}</p>
                      </div>
                    </div>
                  </div>

                  <CardContent className="p-6">
                    {/* Employee Details */}
                    <div className="grid md:grid-cols-2 gap-6 mb-6 pb-6 border-b">
                      <div>
                        <h3 className="font-semibold text-gray-700 mb-3 flex items-center gap-2">
                          <User className="w-4 h-4" />
                          Employee Details
                        </h3>
                        <div className="space-y-2 text-sm">
                          <p><span className="text-gray-500">Name:</span> <span className="font-medium">{selectedPayslip.observer_name}</span></p>
                          <p><span className="text-gray-500">Email:</span> {selectedPayslip.observer_email}</p>
                          <p><span className="text-gray-500">Designation:</span> {selectedPayslip.designation}</p>
                          <p><span className="text-gray-500">PAN:</span> {selectedPayslip.pan}</p>
                        </div>
                      </div>
                      <div>
                        <h3 className="font-semibold text-gray-700 mb-3 flex items-center gap-2">
                          <FileText className="w-4 h-4" />
                          Pay Slip Details
                        </h3>
                        <div className="space-y-2 text-sm">
                          <p><span className="text-gray-500">Pay Slip No:</span> <span className="font-medium">{selectedPayslip.payslip_number}</span></p>
                          <p><span className="text-gray-500">Pay Period:</span> {selectedPayslip.month_name}</p>
                          <p><span className="text-gray-500">Bank A/C:</span> {selectedPayslip.bank_account}</p>
                          <p><span className="text-gray-500">Status:</span> 
                            <span className={`ml-2 px-2 py-0.5 rounded-full text-xs ${
                              selectedPayslip.status === 'paid' ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'
                            }`}>
                              {selectedPayslip.status === 'paid' ? 'Paid' : 'Pending'}
                            </span>
                          </p>
                        </div>
                      </div>
                    </div>

                    {/* Earnings & Deductions */}
                    <div className="grid md:grid-cols-2 gap-6 mb-6">
                      {/* Earnings */}
                      <div>
                        <h3 className="font-semibold text-green-700 mb-3 flex items-center gap-2">
                          <Plus className="w-4 h-4" />
                          Earnings
                        </h3>
                        <div className="space-y-2">
                          <div className="flex justify-between text-sm">
                            <span>Sessions ({selectedPayslip.earnings?.total_sessions} × ₹{selectedPayslip.earnings?.per_session_rate})</span>
                            <span className="font-medium">₹{selectedPayslip.earnings?.base_earnings?.toLocaleString()}</span>
                          </div>
                          {selectedPayslip.earnings?.weekly_bonus > 0 && (
                            <div className="flex justify-between text-sm">
                              <span>Weekly Bonus ({selectedPayslip.weeks_with_bonus} weeks)</span>
                              <span className="font-medium">₹{selectedPayslip.earnings?.weekly_bonus?.toLocaleString()}</span>
                            </div>
                          )}
                          {selectedPayslip.earnings?.rating_bonus > 0 && (
                            <div className="flex justify-between text-sm">
                              <span>High Rating Bonus (Avg: {selectedPayslip.avg_rating})</span>
                              <span className="font-medium">₹{selectedPayslip.earnings?.rating_bonus?.toLocaleString()}</span>
                            </div>
                          )}
                          <div className="flex justify-between pt-2 border-t font-semibold text-green-700">
                            <span>Gross Earnings</span>
                            <span>₹{selectedPayslip.earnings?.gross_earnings?.toLocaleString()}</span>
                          </div>
                        </div>
                      </div>

                      {/* Deductions */}
                      <div>
                        <h3 className="font-semibold text-red-700 mb-3 flex items-center gap-2">
                          <Minus className="w-4 h-4" />
                          Deductions
                        </h3>
                        <div className="space-y-2">
                          {selectedPayslip.deductions?.tds > 0 && (
                            <div className="flex justify-between text-sm">
                              <span>TDS ({selectedPayslip.deductions?.tds_rate}%)</span>
                              <span className="font-medium">₹{selectedPayslip.deductions?.tds?.toLocaleString()}</span>
                            </div>
                          )}
                          {selectedPayslip.deductions?.professional_tax > 0 && (
                            <div className="flex justify-between text-sm">
                              <span>Professional Tax</span>
                              <span className="font-medium">₹{selectedPayslip.deductions?.professional_tax?.toLocaleString()}</span>
                            </div>
                          )}
                          {selectedPayslip.deductions?.total_deductions === 0 && (
                            <p className="text-sm text-gray-500">No deductions</p>
                          )}
                          <div className="flex justify-between pt-2 border-t font-semibold text-red-700">
                            <span>Total Deductions</span>
                            <span>₹{selectedPayslip.deductions?.total_deductions?.toLocaleString()}</span>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Net Pay */}
                    <div className="bg-gradient-to-r from-green-50 to-emerald-50 rounded-xl p-6 border border-green-200">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-gray-600">Net Pay</p>
                          <p className="text-3xl font-bold text-green-700">₹{selectedPayslip.net_earnings?.toLocaleString()}</p>
                        </div>
                        <div className="flex gap-2 print:hidden">
                          <Button variant="outline" onClick={printPayslip}>
                            <Printer className="w-4 h-4 mr-2" />
                            Print
                          </Button>
                          <Button className="bg-green-600 hover:bg-green-700">
                            <Download className="w-4 h-4 mr-2" />
                            Download PDF
                          </Button>
                        </div>
                      </div>
                    </div>

                    {/* Footer */}
                    <div className="mt-6 pt-4 border-t text-center text-xs text-gray-400">
                      <p>This is a computer-generated document. No signature required.</p>
                      <p className="mt-1">
                        Generated on {new Date(selectedPayslip.generated_at).toLocaleString()} | 
                        {selectedPayslip.company?.pan} | {selectedPayslip.company?.tan}
                      </p>
                    </div>
                  </CardContent>
                </Card>
              ) : (
                <Card>
                  <CardContent className="p-12 text-center">
                    <FileText className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                    <h3 className="text-lg font-semibold text-gray-700 mb-2">Select a Pay Slip</h3>
                    <p className="text-gray-500">Choose a pay slip from the history or generate a new one</p>
                  </CardContent>
                </Card>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Print Styles */}
      <style>{`
        @media print {
          body * { visibility: hidden; }
          #payslip-content, #payslip-content * { visibility: visible; }
          #payslip-content { position: absolute; left: 0; top: 0; width: 100%; }
        }
      `}</style>
    </div>
  );
};

export default ObserverPayslips;
